import {
  BaseellaStatus,
  BaseTransaction,
  Response,
  apiAbortController,
} from '@ui/src';
import api from '../../../api/api';
import { ExportBatchEntity } from '../types';

export const exportPspApi = api.injectEndpoints({
  endpoints: (build) => ({
    getExportPspPartners: build.query<
      Response<{ id: string; companyName: string; exportFileType?: string }>,
      string
    >({
      query: (query) => ({
        url: `/v1/psp-partners${query}${query ? '&' : '?'}all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getExportPspAccounts: build.query<
      Response<{ account: { id: string; fullNumber: string }; id: string }>,
      { id: string; query: string }
    >({
      query: ({ id, query }) => ({
        url: `/v1/psp-partners/${id}/primary-accounts${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getExportBatches: build.query<
      Response<ExportBatchEntity>,
      {
        pspPartnerId: string;
        pspAccountId: string;
        take: string;
        cursor?: string;
      }
    >({
      query: (data) => ({
        url: `/v1/exports-batches?pspPartnerId=${
          data.pspPartnerId
        }&pspAccountId=${
          data.pspAccountId
        }&sortProp=createdAt&sortDir=desc&take=${data.take}${
          data.cursor ? `&cursor=${data.cursor}` : ''
        }`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getExportBatchById: build.query<
      { data: ExportBatchEntity },
      { id: string; query: string }
    >({
      query: (data) => ({
        url: `/v1/exports-batches/${data.id}${data.query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createExportBatch: build.mutation<
      { data: ExportBatchEntity },
      {
        transactionsIds: string[];
        pspPartnerId: string;
        pspAccountId: string;
      }
    >({
      query: (body) => ({
        url: '/v1/exports-batches',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getExportOutgoingTransactionsCount: build.query<
      Response<BaseTransaction>,
      {
        pspPartnerId: string;
        pspPartnerAccountNumber: string;
      }
    >({
      query: (data) => ({
        url: `/v1/transactions-outgoing?baseellaStatus=${BaseellaStatus.READY_TO_SEND}&pspPartnerId=${data.pspPartnerId}&pspPartnerAccountNumber=${data.pspPartnerAccountNumber}&all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getExportOutgoingList: build.query<Response<BaseTransaction>, string>({
      query: (query) => ({
        url: `/v1/transactions-outgoing${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getExportBatchFile: build.query<{ data: any }, string>({
      query: (id) => ({
        url: `/v1/exports-batches/${id}/file`,
        responseHandler: (response) => {
          let responseFormatted;

          if (response.status !== 200) {
            responseFormatted = response.json();
          } else {
            responseFormatted = response.blob();
          }

          return responseFormatted;
        },
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useLazyGetExportPspPartnersQuery,
  useLazyGetExportPspAccountsQuery,
  useLazyGetExportBatchesQuery,
  useLazyGetExportBatchByIdQuery,
  useLazyGetExportOutgoingListQuery,
  useCreateExportBatchMutation,
  useLazyGetExportBatchFileQuery,
  useLazyGetExportOutgoingTransactionsCountQuery,
} = exportPspApi;

export const {
  endpoints: {
    getExportPspPartners,
    getExportPspAccounts,
    getExportBatches,
    getExportBatchById,
    createExportBatch,
    getExportOutgoingList,
    getExportBatchFile,
    getExportOutgoingTransactionsCount,
  },
} = exportPspApi;
